// admin/src/components/SessionDetailModal.jsx
import React from 'react';

/**
 * SessionDetailModal - Shows detailed view of a quiz session
 * 
 * Props:
 * - session: session object with all computed data
 * - isOpen: boolean
 * - onClose: function
 */
export default function SessionDetailModal({ session, isOpen, onClose }) {
  if (!isOpen || !session) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTag = (slug) => {
    if (!slug) return '';
    return slug.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 700 }}
      >
        <div className="modal-header" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <h2 style={{ margin: 0, marginBottom: 8 }}>{session.personalizedTitle || 'Session Details'}</h2>
              
              {/* User Information - Prominent */}
              {(session.firstName || session.email) ? (
                <div style={{ 
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                  borderRadius: 8,
                  border: '2px solid #86efac',
                  marginBottom: 12
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', alignItems: 'center' }}>
                    {session.firstName && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 600 }}>Name:</div>
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
                          {session.firstName} {session.lastName || ''}
                        </div>
                      </>
                    )}
                    
                    {session.email && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 600 }}>Email:</div>
                        <div style={{ 
                          fontSize: '0.9rem', 
                          color: '#374151',
                          fontFamily: 'monospace',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8
                        }}>
                          {session.email}
                          {session.emailOptIn && (
                            <span style={{ 
                              background: '#10b981',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: 12,
                              fontSize: '0.7rem',
                              fontWeight: 700
                            }}>
                              ✓ OPTED IN
                            </span>
                          )}
                        </div>
                      </>
                    )}
                    
                    {session.degreeLevel && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 600 }}>Degree:</div>
                        <div style={{ fontSize: '0.9rem', color: '#111827', textTransform: 'capitalize' }}>
                          {session.degreeLevel.replace(/-/g, ' ')}
                        </div>
                      </>
                    )}
                    
                    {session.ageRange && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 600 }}>Age:</div>
                        <div style={{ fontSize: '0.9rem', color: '#111827' }}>
                          {session.ageRange}
                        </div>
                      </>
                    )}
                    
                    {session.leadCapturedAt && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 600 }}>Lead Captured:</div>
                        <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                          {formatDate(session.leadCapturedAt)}
                        </div>
                      </>
                    )}
                    
                    {session.emailCapturedAt && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 600 }}>Email Captured:</div>
                        <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                          {formatDate(session.emailCapturedAt)}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ 
                  padding: '10px 14px',
                  background: '#fef2f2',
                  borderRadius: 6,
                  border: '1px solid #fecaca',
                  fontSize: '0.85rem',
                  color: '#991b1b',
                  marginBottom: 12
                }}>
                  ⚠️ No contact information captured
                </div>
              )}
              
              {/* Session ID */}
              <div style={{ 
                fontSize: '0.7rem', 
                color: '#9ca3af',
                fontFamily: 'monospace'
              }}>
                Session ID: {session.sessionId}
              </div>
            </div>
            
            <button 
              className="modal-close" 
              onClick={onClose}
              aria-label="Close"
              style={{ marginLeft: 16 }}
            >
              ×
            </button>
          </div>
        </div>

        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          
          {/* Personalized Title */}
          {session.personalizedTitle && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ 
                fontSize: '0.95rem', 
                fontWeight: 700, 
                marginBottom: 8,
                color: '#111827'
              }}>
                Personalized Title
              </h3>
              <div style={{
                padding: 16,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 12,
                color: 'white',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {session.personalizedTitle}
                </div>
              </div>
            </div>
          )}

          {/* Mindset Counts */}
          {session.mindsetCounts && session.mindsetCounts.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ 
                fontSize: '0.95rem', 
                fontWeight: 700, 
                marginBottom: 12,
                color: '#111827'
              }}>
                Mindset Profile
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {session.mindsetCounts.map((item, idx) => (
                  <div 
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      background: idx === 0 ? '#eff6ff' : 'white',
                      border: `1px solid ${idx === 0 ? '#3b82f6' : '#e5e7eb'}`,
                      borderRadius: 8
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                      <span style={{ 
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: '#6b7280',
                        minWidth: 24
                      }}>
                        #{idx + 1}
                      </span>
                      <span style={{ 
                        fontWeight: idx === 0 ? 700 : 500,
                        color: idx === 0 ? '#1e40af' : '#374151',
                        fontSize: '0.9rem'
                      }}>
                        {formatTag(item.tag)}
                      </span>
                    </div>
                    <div style={{
                      background: idx === 0 ? '#3b82f6' : '#e5e7eb',
                      color: idx === 0 ? 'white' : '#374151',
                      padding: '4px 12px',
                      borderRadius: 999,
                      fontWeight: 600,
                      fontSize: '0.85rem'
                    }}>
                      {item.count}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Area Counts */}
          {session.areaCounts && session.areaCounts.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ 
                fontSize: '0.95rem', 
                fontWeight: 700, 
                marginBottom: 12,
                color: '#111827'
              }}>
                Areas of Interest
              </h3>
              <div className="adm-pillrow" style={{ gap: 8 }}>
                {session.areaCounts.slice(0, 10).map((item, idx) => (
                  <span 
                    key={idx}
                    className="adm-tag"
                    style={{
                      background: idx < 3 ? '#e0f2fe' : '#f3f4f6',
                      color: idx < 3 ? '#075985' : '#6b7280',
                      border: idx < 3 ? '1px solid #0ea5e9' : '1px solid #e5e7eb',
                      fontSize: '0.8rem',
                      padding: '6px 10px'
                    }}
                  >
                    {formatTag(item.tag)} <strong>({item.count})</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Content Counts */}
          {session.contentCounts && session.contentCounts.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ 
                fontSize: '0.95rem', 
                fontWeight: 700, 
                marginBottom: 12,
                color: '#111827'
              }}>
                Content Preferences
              </h3>
              <div className="adm-pillrow" style={{ gap: 8 }}>
                {session.contentCounts.map((item, idx) => (
                  <span 
                    key={idx}
                    className="adm-badge adm-badge--neutral"
                    style={{ fontSize: '0.8rem' }}
                  >
                    {formatTag(item.tag)} ({item.count})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Programs */}
          {session.selectedProgramIds && session.selectedProgramIds.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ 
                fontSize: '0.95rem', 
                fontWeight: 700, 
                marginBottom: 12,
                color: '#111827'
              }}>
                🎓 Recommended Programs ({session.selectedProgramIds.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {session.selectedProgramIds.map((programId, idx) => {
                  // If programs are populated, show full details
                  if (typeof programId === 'object' && programId.title) {
                    return (
                      <div 
                        key={programId._id || idx}
                        style={{
                          padding: '12px 14px',
                          background: idx === 0 ? '#eff6ff' : 'white',
                          border: `1px solid ${idx === 0 ? '#3b82f6' : '#e5e7eb'}`,
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12
                        }}
                      >
                        <span style={{ 
                          fontSize: '1.25rem',
                          fontWeight: 700,
                          color: idx === 0 ? '#1e40af' : '#6b7280',
                          minWidth: 24
                        }}>
                          #{idx + 1}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: 600,
                            color: '#111827',
                            fontSize: '0.95rem',
                            marginBottom: 4
                          }}>
                            {programId.title}
                          </div>
                          {programId.areasOfStudy && programId.areasOfStudy.length > 0 && (
                            <div style={{ 
                              fontSize: '0.75rem',
                              color: '#6b7280'
                            }}>
                              {programId.areasOfStudy.slice(0, 3).map(formatTag).join(' • ')}
                              {programId.areasOfStudy.length > 3 && ' • ...'}
                            </div>
                          )}
                        </div>
                        {programId.permalink && (
                          <a 
                            href={programId.permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '0.8rem',
                              color: '#3b82f6',
                              textDecoration: 'none',
                              fontWeight: 600
                            }}
                          >
                            View →
                          </a>
                        )}
                      </div>
                    );
                  }
                  
                  // Fallback: just show ID if not populated
                  return (
                    <div 
                      key={programId._id || programId || idx}
                      style={{
                        padding: '10px 12px',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: 6,
                        fontSize: '0.85rem',
                        color: '#6b7280',
                        fontFamily: 'monospace'
                      }}
                    >
                      #{idx + 1}: {String(programId._id || programId)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Result Content Tags */}
          {session.resultContentTags && session.resultContentTags.length > 0 && (
            <div>
              <h3 style={{ 
                fontSize: '0.95rem', 
                fontWeight: 700, 
                marginBottom: 12,
                color: '#111827'
              }}>
                📄 Result Content ({session.resultContentTags.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {session.resultContentTags.map((tag, idx) => (
                  <div 
                    key={idx}
                    style={{
                      padding: '10px 14px',
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: '0.9rem',
                      color: '#111827'
                    }}
                  >
                    {formatTag(tag)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn--primary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}